import { useState, useCallback, useMemo } from 'react';
import releaseNotes from '../release-notes.json';
import { LAST_SEEN_VERSION_KEY, APP_VERSION } from '../release-notes-constants';
import type { ReleaseNotesData, LocalizedHighlight } from '../release-notes-types';

function readLastSeenVersion(): string | null {
  try {
    return localStorage.getItem(LAST_SEEN_VERSION_KEY);
  } catch {
    return null;
  }
}

function writeLastSeenVersion(version: string): void {
  try {
    localStorage.setItem(LAST_SEEN_VERSION_KEY, version);
  } catch {
    // quota exceeded or private mode — ignore
  }
}

function compareVersions(a: string, b: string): number {
  // Handle version scheme transition (1.0.x → 0.x.y):
  // If one starts with "1." and the other with "0.", the "0." is newer (new scheme)
  const aOld = a.startsWith('1.');
  const bOld = b.startsWith('1.');
  if (aOld && !bOld) return -1; // a is old scheme, b is newer
  if (!aOld && bOld) return 1;  // a is new scheme, b is old
  return a.localeCompare(b, undefined, { numeric: true });
}

export interface UseReleaseNotesReturn {
  hasUpdate: boolean;
  currentHighlight: LocalizedHighlight | null;
  currentVersion: string | undefined;
  versions: ReleaseNotesData;
  markVersionSeen: () => void;
  showWhatsNew: boolean;
  setShowWhatsNew: (show: boolean) => void;
}

export function useReleaseNotes(): UseReleaseNotesReturn {
  const versions = releaseNotes as ReleaseNotesData;
  const currentVersion = APP_VERSION;

  const [lastSeenVersion, setLastSeenVersion] = useState<string | null>(readLastSeenVersion);
  const [showWhatsNew, setShowWhatsNew] = useState(false);

  const hasUpdate = useMemo(() => {
    if (!currentVersion) return false;
    if (!lastSeenVersion) return true;
    return compareVersions(lastSeenVersion, currentVersion) < 0;
  }, [currentVersion, lastSeenVersion]);

  const currentHighlight = useMemo(() => {
    if (!currentVersion) return null;
    const entry = versions.find((v) => v.version === currentVersion);
    return entry?.highlight ?? null;
  }, [currentVersion, versions]);

  const markVersionSeen = useCallback(() => {
    if (!currentVersion) return;
    setLastSeenVersion(currentVersion);
    writeLastSeenVersion(currentVersion);
  }, [currentVersion]);

  return {
    hasUpdate,
    currentHighlight,
    currentVersion,
    versions,
    markVersionSeen,
    showWhatsNew,
    setShowWhatsNew,
  };
}
